import { useEffect, useRef, useState } from 'react';
import { Button, SpinLoading, Toast } from 'antd-mobile';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loginMemberFromWhatsApp } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  clearWsToken,
  isWsMemberNotFoundError,
  isWsPhoneRequiredError,
  isWsTokenError,
  resolveWsTokenForWsRoute,
} from '../utils/wsToken';
import { useWsQuickRegisterGate } from '../hooks/useWsQuickRegisterGate';

export default function LoginFromWsPage() {
  const { loginFromWsApiResult } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [apiFailed, setApiFailed] = useState(false);
  const missingToastShown = useRef(false);

  const wsToken = resolveWsTokenForWsRoute(searchParams);
  const { gateReady } = useWsQuickRegisterGate(wsToken);
  /** 单独依赖，避免整个 `searchParams` 引用变化导致 effect 反复取消请求 */
  const redirectTarget = searchParams.get('redirect');

  useEffect(() => {
    if (!wsToken) {
      if (!missingToastShown.current) {
        missingToastShown.current = true;
        Toast.show({ icon: 'fail', content: 'Missing login link. Please open this page from WhatsApp.' });
      }
      return undefined;
    }
    if (!gateReady) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const loginResult = await loginMemberFromWhatsApp(wsToken);
        if (cancelled) {
          return;
        }

        const userProfile = await loginFromWsApiResult(loginResult);

        if (userProfile?.id == null || userProfile?.id === '') {
          Toast.show({ icon: 'fail', content: 'Login failed: invalid member data' });
          setApiFailed(true);
          return;
        }

        clearWsToken();
        Toast.show({ icon: 'success', content: 'Logged in successfully' });

        const pendingSignCode = localStorage.getItem('pending-sign-code');
        const pendingWorkerCode = localStorage.getItem('pending-worker-code');
        const pendingSpring2026Code = localStorage.getItem('pending-spring2026-code');

        if (redirectTarget) {
          navigate(redirectTarget, { replace: true });
        } else if (pendingSignCode) {
          localStorage.removeItem('pending-sign-code');
          navigate(`/sign?code=${encodeURIComponent(pendingSignCode)}`, { replace: true });
        } else if (pendingWorkerCode) {
          localStorage.removeItem('pending-worker-code');
          navigate(`/worker?code=${encodeURIComponent(pendingWorkerCode)}`, { replace: true });
        } else if (pendingSpring2026Code) {
          localStorage.removeItem('pending-spring2026-code');
          navigate(`/spring2026?code=${encodeURIComponent(pendingSpring2026Code)}`, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } catch (e) {
        if (cancelled) {
          return;
        }
        if (isWsMemberNotFoundError(e?.message)) {
          navigate(`/registerfromws?wstoken=${encodeURIComponent(wsToken)}`, { replace: true });
          return;
        }
        if (isWsPhoneRequiredError(e?.message, e?.code)) {
          clearWsToken();
          navigate('/register', { replace: true });
          return;
        }
        if (isWsTokenError(e?.message)) {
          clearWsToken();
        }
        Toast.show({ icon: 'fail', content: e?.message || 'Login failed, please try again later' });
        setApiFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [wsToken, gateReady, loginFromWsApiResult, navigate, redirectTarget]);

  if (!wsToken) {
    return (
      <div className="loading-screen">
        <span>Invalid link</span>
        <Button
          color="primary"
          onClick={() => {
            clearWsToken();
            navigate('/login', { replace: true });
          }}
        >
          Go to login
        </Button>
      </div>
    );
  }

  if (wsToken && !gateReady) {
    return (
      <div className="loading-screen">
        <SpinLoading color="primary" />
        <span>Signing you in…</span>
      </div>
    );
  }

  if (apiFailed) {
    return (
      <div className="loading-screen">
        <span>Could not complete WhatsApp login</span>
        <Button
          color="primary"
          fill="outline"
          onClick={() =>
            navigate(`/registerfromws?wstoken=${encodeURIComponent(wsToken)}`, { replace: true })
          }
        >
          Register first
        </Button>
        <Button
          color="primary"
          onClick={() => {
            clearWsToken();
            navigate('/login', { replace: true });
          }}
        >
          Go to login
        </Button>
      </div>
    );
  }

  return (
    <div className="loading-screen">
      <SpinLoading color="primary" />
      <span>Signing you in…</span>
    </div>
  );
}
